import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { LocalStorageKeys } from '../../helpers/constants';
import { PosthogService } from '../../services/posthog.service';
import { HttpService } from '../../services/http-service';
import { ITranslation } from '../../models/translations/translations';

type OnboardingPath = 'upload' | 'video' | null;

export type ChecklistStepAction = 'upload_image' | 'convert_pattern' | 'save';

interface ChecklistStep {
  id: string;
  label: string;
  completed: boolean;
  action?: ChecklistStepAction; // Action to trigger when clicked
}

@Component({
  selector: 'app-onboarding-checklist',
  templateUrl: './onboarding-checklist.component.html',
  styleUrls: ['./onboarding-checklist.component.scss']
})
export class OnboardingChecklistComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  @Input() onboardingPath: OnboardingPath = null;
  @Output() checklistClosed = new EventEmitter<void>();
  @Output() stepClicked = new EventEmitter<ChecklistStepAction>();
  @Output() nextStepChanged = new EventEmitter<ChecklistStepAction | null>();

  isMinimized = false;
  isVisible = false; // Hidden until path is selected
  showConfetti = false;
  private previousNextStep: ChecklistStepAction | null = null;
  confettiPieces: Array<{
    left: number;
    animationDelay: number;
    backgroundColor: string;
    size: number;
  }> = [];

  steps: ChecklistStep[] = [];

  constructor(
    private posthog: PosthogService,
    private httpService: HttpService
  ) {}

  get t(): ITranslation {
    return this.httpService.getTransation();
  }

  // Tutorial steps (image-to-pattern)
  private get uploadTutorialSteps(): ChecklistStep[] {
    return [
      { id: 'signin', label: this.t.ChecklistStepSignedIn, completed: false },
      { id: 'upload_image', label: this.t.ChecklistStepUploadImage, completed: false, action: 'upload_image' },
      { id: 'convert_pattern', label: this.t.ChecklistStepConvertPattern, completed: false, action: 'convert_pattern' },
      { id: 'save', label: this.t.ChecklistStepSaveWork, completed: false, action: 'save' }
    ];
  }

  ngOnInit(): void {
    // Check if user already activated
    const activated = localStorage.getItem(LocalStorageKeys.OnboardingChecklistCompleted);
    if (activated === 'true') {
      this.isVisible = false;
      return;
    }

    // Load saved path from localStorage
    const savedPath = localStorage.getItem('onboarding_path') as OnboardingPath;
    if (savedPath) {
      this.onboardingPath = savedPath;
      // Show checklist ONLY for 'upload' path
      this.isVisible = savedPath === 'upload';
    }

    // Initialize steps based on path
    this.initializeSteps();

    // Load saved state from localStorage
    this.loadState();

    // Check for initial next step and emit if applicable
    setTimeout(() => this.checkAndEmitNextStepChange(), 500);
  }

  /**
   * Initialize checklist steps for tutorial
   */
  private initializeSteps(): void {
    this.steps = this.onboardingPath === 'upload' ? this.uploadTutorialSteps.map((step) => ({ ...step })) : [];
    console.log('[Checklist] Initialized with tutorial steps');
  }

  /**
   * Set the onboarding path and update steps
   * Can be called after component initialization
   */
  setOnboardingPath(path: OnboardingPath): void {
    this.onboardingPath = path;

    // Save path to localStorage
    if (path) {
      localStorage.setItem('onboarding_path', path);
    }

    this.initializeSteps();
    this.loadState(); // Reload state after changing steps

    // Make checklist visible ONLY for 'upload' path
    this.isVisible = path === 'upload';

    console.log('[Checklist] Path set to:', path, 'Visible:', this.isVisible);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get completedCount(): number {
    return this.steps.filter(s => s.completed).length;
  }

  get totalCount(): number {
    return this.steps.length;
  }

  get isCompleted(): boolean {
    return this.steps.every(s => s.completed);
  }

  get progressPercentage(): number {
    return Math.round((this.completedCount / this.totalCount) * 100);
  }

  completeStep(stepId: string): void {
    const step = this.steps.find(s => s.id === stepId);

    if (step && !step.completed) {
      step.completed = true;
      this.saveState();

      // Check if next step changed and emit event
      this.checkAndEmitNextStepChange();

      // Check if all steps completed (activation!)
      if (this.isCompleted) {
        this.onChecklistCompleted();
      }
    }
  }

  /**
   * Check if the next step has changed and emit event
   * This is used to auto-open menus when a new step becomes active
   */
  private checkAndEmitNextStepChange(): void {
    const nextStep = this.steps.find(s => !s.completed && s.action);
    const currentNextAction = nextStep?.action || null;

    if (currentNextAction !== this.previousNextStep) {
      this.previousNextStep = currentNextAction;
      this.nextStepChanged.emit(currentNextAction);
      console.log('[Checklist] Next step changed to:', currentNextAction);
    }
  }

  private onChecklistCompleted(): void {
    this.showConfetti = true;


    localStorage.setItem('onboarding_checklist_completed', 'true');

    // Trigger awesome canvas-confetti animation
    this.celebrateWithConfetti();

    // Hide checklist immediately when onboarding is complete
    this.isVisible = false;
  }

  /**
   * Celebrate with custom confetti animation
   */
  private celebrateWithConfetti(): void {
    const colors = ['#FF4500', '#FFD700', '#00CED1', '#FF1493', '#32CD32', '#9370DB'];
    const confettiCount = 100;
    
    // Generate confetti pieces with random properties
    this.confettiPieces = [];
    for (let i = 0; i < confettiCount; i++) {
      this.confettiPieces.push({
        left: Math.random() * 100, // Random horizontal position (0-100%)
        animationDelay: Math.random() * 0.5, // Random delay (0-0.5s)
        backgroundColor: colors[Math.floor(Math.random() * colors.length)],
        size: Math.random() * 6 + 6 // Random size (6-12px)
      });
    }
  }

  toggleMinimize(): void {
    this.isMinimized = !this.isMinimized;
  }

  /**
   * Handle step click - emit action to parent component
   * Only emit for incomplete steps with actions
   */
  onStepClick(step: ChecklistStep): void {
    // Don't trigger action for completed steps or steps without actions
    if (step.completed || !step.action) {
      return;
    }

    // Track the click
    this.posthog.captureEvent('onboarding_checklist_step_clicked', {
      step_id: step.id,
      step_action: step.action,
      onboarding_path: this.onboardingPath,
    });

    // Emit the action to parent
    this.stepClicked.emit(step.action);
  }

  /**
   * Check if a step is the next actionable step
   */
  isNextStep(step: ChecklistStep): boolean {
    if (step.completed || !step.action) {
      return false;
    }
    // Find the first incomplete step with an action
    const nextStep = this.steps.find(s => !s.completed && s.action);
    return nextStep?.id === step.id;
  }

  /**
   * Close the checklist entirely (user-driven skip)
   */
  closeChecklist(): void {
    localStorage.setItem(LocalStorageKeys.OnboardingChecklistCompleted, 'true');
    this.isVisible = false;
    this.checklistClosed.emit();

    this.posthog.captureEvent('onboarding_checklist_closed', {
      reason: 'manual_close',
      onboarding_path: this.onboardingPath || 'none',
      steps_completed: this.completedCount,
      total_steps: this.totalCount,
    });
  }

  private saveState(): void {
    const state = this.steps.map(s => ({
      id: s.id,
      completed: s.completed
    }));
    localStorage.setItem('onboarding_checklist_state', JSON.stringify(state));
  }

  private loadState(): void {
    const savedState = localStorage.getItem('onboarding_checklist_state');

    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        state.forEach((saved: any) => {
          const step = this.steps.find(s => s.id === saved.id);
          if (step) {
            step.completed = saved.completed;
          }
        });
      } catch (e) {
        console.error('Failed to load checklist state:', e);
      }
    }
  }

}
